// frontend/src/riskSchema.js
// Canonical risk thresholds + schema data used by both UI and engine.

// ===== Canonical thresholds (single source of truth) =====
export const RISK_THRESHOLDS = {
  low: { min: 0, max: 7 },
  medium: { min: 8, max: 14 },
  high: { min: 15, max: 22 },
  very_high: { min: 23, max: 30 },
};

// Optional helper for UI tooltips
export function thresholdsToRanges(t = RISK_THRESHOLDS) {
  return {
    low: `0–${t.low.max}`,
    medium: `${t.medium.min}–${t.medium.max}`,
    high: `${t.high.min}–${t.high.max}`,
    very_high: `${t.very_high.min}+`,
  };
}

// ===== Section-specific answer points (defaults) =====
// Section A (documentation): Y=0, N=1, NA=0
// Section B (risk factors):  Y=1, N=0, NA=0
export const SECTION_ANSWER_POINTS = {
  A: { Y: 0, N: 1, NA: 0 },
  B: { Y: 1, N: 0, NA: 0 },
};

// Legacy/global (kept for backwards compatibility only)
export const ANSWER_POINTS = { Y: 0, N: 0, NA: 0 };

// ===== Questions =====
// (Truncated comments; these remain identical to what you had.)
// A
export const QUESTIONS_A = [
  { id: "A1", section: "A", header: "Corporate file", label: "Application form", type: "yn", weight: 1 },
  { id: "A2", section: "A", header: "Corporate file", label: "Date incorporation/Ministerial deed", type: "yn", weight: 1 },
  { id: "A3", section: "A", header: "Corporate file", label: "Listing of services provided by the trust service provider to the international company", type: "yn", weight: 1 },
  {
    id: "A4",
    section: "A",
    header: "Corporate file",
    label: "Existing agreements between the trust service provider and the international company, such as management, administrative, and principal party agreements",
    type: "yn",
    weight: 1,
  },
  { id: "A5", section: "A", header: "Corporate file", label: "Foreign Exchange Exemption issued by the Centrale Bank van Curaçao en Sint Maarten", type: "yn", weight: 1 },
  {
    id: "A6",
    section: "A",
    header: "Corporate file",
    label: "License to Establish a Business and Management License issued by the Department of Economic Affairs",
    type: "yn",
    weight: 1,
  },
  { id: "A7", section: "A", header: "Corporate file", label: "Articles of Incorporation", type: "yn", weight: 1 },
  { id: "A8", section: "A", header: "Corporate file", label: "Amendments to the Articles of Incorporation", type: "yn", weight: 1 },
  { id: "A9", section: "A", header: "Corporate file", label: "Original excerpt from the Chamber of Commerce (not older than 3 years)", type: "yn", weight: 1 },
  { id: "A10", section: "A", header: "Corporate file", label: "Recent copy excerpt from Chamber of Commerce (website printout)", type: "yn", weight: 1 },
  { id: "A11", section: "A", header: "Corporate file", label: "Group holding structure", type: "yn", weight: 1 },
  { id: "A12", section: "A", header: "Corporate file", label: "Shareholders register (Register of partnership)", type: "yn", weight: 1 },
  { id: "A13", section: "A", header: "Corporate file", label: "Share transfer agreement (after establishment)", type: "yn", weight: 1 },
  { id: "A14", section: "A", header: "Corporate file", label: "Deposit Agreement (if applicable)", type: "yn", weight: 1 },
  { id: "A15", section: "A", header: "Corporate file", label: "Official proof of the deposited bearer shares", type: "yn", weight: 1 },
  { id: "A16", section: "A", header: "Corporate file", label: "Additional capital", type: "yn", weight: 1 },
  { id: "A17", section: "A", header: "Corporate file", label: "Source of wealth form", type: "yn", weight: 1 },
  { id: "A18", section: "A", header: "Corporate file", label: "Source of funds form", type: "yn", weight: 1 },
  { id: "A19", section: "A", header: "Corporate file", label: "Resolutions resulting from investigations on source of funds", type: "yn", weight: 1 },
  { id: "A20", section: "A", header: "Corporate file", label: "Transactions profile", type: "yn", weight: 1 },

  { id: "A21", section: "A", header: "KYC information", label: "Original Certified Copy of valid identification document of UBO", type: "yn", weight: 1 },
  { id: "A22", section: "A", header: "KYC information", label: "Original certified copy of valid identification document of shareholder", type: "yn", weight: 1 },
  { id: "A23", section: "A", header: "KYC information", label: "Original certified copy of valid identification document of management member", type: "yn", weight: 1 },
  {
    id: "A24",
    section: "A",
    header: "KYC information",
    label: "Original certified copy of valid identification document of members of the board of supervisory directors",
    type: "yn",
    weight: 1,
  },
  { id: "A25", section: "A", header: "KYC information", label: "Original Bank Reference Letter UBO", type: "yn", weight: 1 },
  { id: "A26", section: "A", header: "KYC information", label: "Original Bank Reference Letter shareholder", type: "yn", weight: 1 },
  { id: "A27", section: "A", header: "KYC information", label: "Original Bank Reference Letter management members", type: "yn", weight: 1 },
  { id: "A28", section: "A", header: "KYC information", label: "Original Bank Reference Letter members of the board of supervisory directors", type: "yn", weight: 1 },
  { id: "A29", section: "A", header: "KYC information", label: "Original certified copy of utility bill of UBO", type: "yn", weight: 1 },
  { id: "A30", section: "A", header: "KYC information", label: "Original certified copy of utility bill of shareholder", type: "yn", weight: 1 },
  { id: "A31", section: "A", header: "KYC information", label: "Original certified copy of utility bill of management members", type: "yn", weight: 1 },
  { id: "A32", section: "A", header: "KYC information", label: "Original certified copy of utility bill of members of the board of supervisory directors", type: "yn", weight: 1 },

  { id: "A33", section: "A", header: "Tax", label: "Tax ruling", type: "yn", weight: 1 },
  { id: "A34", section: "A", header: "Tax", label: "Up to date ruling available or extension request", type: "yn", weight: 1 },
  { id: "A35", section: "A", header: "Tax", label: "Profit tax return available", type: "yn", weight: 1 },
  { id: "A36", section: "A", header: "Tax", label: "Profit tax return filing in a timely matter", type: "yn", weight: 1 },

  { id: "A37", section: "A", header: "Bank account information", label: "Extraordinary meeting to open bank account", type: "yn", weight: 1 },
  { id: "A38", section: "A", header: "Bank account information", label: "Bank account opening forms", type: "yn", weight: 1 },
  { id: "A39", section: "A", header: "Bank account information", label: "Signature card", type: "yn", weight: 1 },
  { id: "A40", section: "A", header: "Bank account information", label: "Bank statements available", type: "yn", weight: 1 },
  { id: "A41", section: "A", header: "Bank account information", label: "Outside directors sole signatory (Limited partner)", type: "yn", weight: 1 },
  { id: "A42", section: "A", header: "Bank account information", label: "Joint signatory with outside directors (Limited partner)", type: "yn", weight: 1 },
  { id: "A43", section: "A", header: "Bank account informaation", label: "Sole signatory management members (board member being G-Trust)", type: "yn", weight: 1 },

  { id: "A44", section: "A", header: "Annual accounts", label: "Annual accounts available", type: "yn", weight: 1 },
  {
    id: "A45",
    section: "A",
    header: "Annual accounts",
    label: "Recent annual accounts approved by management in a timely manner according to articles of incorporation",
    type: "yn",
    weight: 1,
  },
  { id: "A46", section: "A", header: "Annual accounts", label: "Participation in annual accounts", type: "yn", weight: 1 },

  { id: "A47", section: "A", header: "Annual General Meeting", label: "Annual General Meeting available", type: "yn", weight: 1 },
  { id: "A48", section: "A", header: "Annual General Meeting", label: "Extraordinary meeting available", type: "yn", weight: 1 },
  { id: "A49", section: "A", header: "Annual General Meeting", label: "Recent annual general meeting held", type: "yn", weight: 1 },
  {
    id: "A50",
    section: "A",
    header: "Annual General Meeting",
    label: "Annual General Meeting held in a timely matter according to articles of incorporation",
    type: "yn",
    weight: 1,
  },
  { id: "A51", section: "A", header: "Annual General Meeting", label: "Discharge of management members", type: "yn", weight: 1 },
];

// B
export const QUESTIONS_B = [
  { id: "B1", section: "B", header: "Customer risk", label: "Is the customer and their families and associate a Politically exposed persons (PEPs)?", type: "yn", weight: 1 },
  {
    id: "B2",
    section: "B",
    header: "Customer risk",
    label: "Can the customer business be categorized as a cash and cash equivalent intensive businesses (e.g. money remitters, casinos, (internet) gambling businesses)?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B3",
    section: "B",
    header: "Customer risk",
    label: "Can the engagement of customer business activities be regarded as sensitive (e.g. porn, arms trading, provision of military security services)?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B4",
    section: "B",
    header: "Customer risk",
    label: "Does the customer structure/nature of entity make it difficult to identify and verify the true owner and controlling interests?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B5",
    section: "B",
    header: "Customer risk",
    label: "Is the customer a charity and non-profit organization which is not subjected to monitoring or supervision?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B6",
    section: "B",
    header: "Customer risk",
    label:
      "Is the customer a financial institution, designated non-financial businesses, or profession that is not subject to adequate AML/CFT laws and measures and not adequately supervised?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B7",
    section: "B",
    header: "Customer risk",
    label: "Does the customer’s business require undue secrecy or appear to break audit trails (layering)?",
    type: "yn",
    weight: 1,
  },
  { id: "B8", section: "B", header: "Customer risk", label: "Are there significant transactions taking place from time to time?", type: "yn", weight: 1 },
  { id: "B9", section: "B", header: "Customer risk", label: "Does the way the account is operated make it possible for material changes to take place?", type: "yn", weight: 1 },
  { id: "B10", section: "B", header: "Customer risk", label: "Does the client documentation change substantially?", type: "yn", weight: 1 },
  { id: "B11", section: "B", header: "Customer risk", label: "Is there insufficient information available on the (existing) customer?", type: "yn", weight: 1 },

  { id: "B12", section: "B", header: "Product/service risk", label: "Does the customer provide private banking activities?", type: "yn", weight: 1 },
  {
    id: "B13",
    section: "B",
    header: "Product/service risk",
    label: "Does the customer make or receive payments from unassociated or unknown third parties?",
    type: "yn",
    weight: 1,
  },
  { id: "B14", section: "B", header: "Product/service risk", label: "Does the customer make the receipt and transmission of cash proceeds possible?", type: "yn", weight: 1 },
  {
    id: "B15",
    section: "B",
    header: "Product/service risk",
    label: "Does the customer provide services that conceal the beneficial ownership from competent authorities?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B16",
    section: "B",
    header: "Product/service risk",
    label: "Does the customer perform transactions or provide services with no apparent business/economic/tax/legal reason?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B17",
    section: "B",
    header: "Product/service risk",
    label: "Does the customer request to pay extraordinary fees for services which would not normally warrant such a premium?",
    type: "yn",
    weight: 1,
  },

  {
    id: "B18",
    section: "B",
    header: "Country & geographic risk",
    label: "Is the country where the customer provides services subject to UN/EU sanctions or embargoes?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B19",
    section: "B",
    header: "Country & geographic risk",
    label: "Has the country been identified by FATF/FSRBs as lacking appropriate AML/CFT laws, regulations and other measures?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B20",
    section: "B",
    header: "Country & geographic risk",
    label: "Has the country been identified by credible sources as funding/supporting terrorist activities or hosting designated organizations?",
    type: "yn",
    weight: 1,
  },

  {
    id: "B21",
    section: "B",
    header: "Delivery channels risk",
    label: "Does the customer rely on introducers/intermediaries for CDD that don’t meet all criteria?",
    type: "yn",
    weight: 1,
  },
  { id: "B22", section: "B", header: "Delivery channels risk", label: "Was relationship establishment done remotely (non-face-to-face)?", type: "yn", weight: 1 },
  {
    id: "B23",
    section: "B",
    header: "Delivery channels risk",
    label: "Is the relationship/transactions controlled remotely (e.g., straight-through processing)?",
    type: "yn",
    weight: 1,
  },
  {
    id: "B24",
    section: "B",
    header: "Delivery channels risk",
    label: "Do pooled relationships with intermediaries create anonymity via commingled assets/funds?",
    type: "yn",
    weight: 1,
  },
];

// Combined list (what the UI uses)
export const QUESTIONS = [...QUESTIONS_A, ...QUESTIONS_B];
